import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductShareLink from "@insite/content-library/Components/ProductShareLink";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import List from "@insite/mobius/Icons/List";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import Share from "@insite/mobius/Icons/Share";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusCircle from "@SupplyLand/MobiusOverrides/MinusCircle";
import parse from "html-react-parser";
import cloneDeep from "lodash/cloneDeep";
import { useState } from "react";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    tabContainer?: TabPresentationProps;
    tabInfoContainer?: TabPresentationProps;
    tabDocumentContainer?: TabPresentationProps;
    tabSpecificationsContainer?: TabPresentationProps;
    hiddenTabContainer?: TabPresentationProps;
    container?: GridContainerProps;
    docContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    optionsGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    optionsIcon?: IconProps;
    tabContent?: TypographyPresentationProps;
    hiddenTabContent?: TypographyPresentationProps;
    documentNameText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css``,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    tabInfoContainer: {
        css: css`
            text-transform: capitalize;
        `,
    },
    docContainer: {
        css: css`
            & ${GridOffset} {
                display: grid;
            }
        `,
    },
    hiddenTabContainer: {
        css: css`
            display: none;
        `,
    },
    tabContent: {
        css: css`
            cursor: pointer;
            padding-left: 5px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
            grid-auto-flow: column;
        `,
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    optionsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            grid-template-rows: 1fr 1fr 1fr 1fr 1fr;
            grid-auto-flow: column;
            align-items: center;
        `,
    },
    optionsIcon: {
        color: "#686868",
        css: css`
            margin-left: 5px;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsCollapsibleTabs: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties, canAddToWishlist },
}) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    interface TabItems {
        [key: string]: boolean;
    }

    const [state, setState] = React.useState({} as TabItems);
    const [currentTab, setMyCurrentTab] = useState("");

    React.useEffect(() => {
        setCurrentTab("FAQs");
    }, []);

    const renderingSpecifications = specifications && specifications.length > 0;
    const tabs: TabItems = {};

    if (renderingSpecifications) {
        specifications?.forEach(spec => {
            tabs[spec.name] = false;
        });
    }
    const specs = cloneDeep(specifications?.slice());
    const sortedSpecifications = specs?.sort((a, b) => a.sortOrder - b.sortOrder);

    tabs["Documents"] = false;
    tabs["Options"] = false;

    const setCurrentTab = (tabName: string) => {
        Object.keys(tabs).forEach((tabItem: string) => {
            if (tabItem === tabName) {
                setState({ [tabName]: !state[tabName] });
            } else if (state[tabName] === true) {
                setState({ [tabName]: false });
            }
        });
        setMyCurrentTab(tabName);
    };

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        if (tabKey) {
            setCurrentTab(tabKey);
        }
    };

    const onClickAccordionSection = (tabKey: string) => () => {
        if (tabKey) {
            const keys = Object.keys(state);
            let previousTab = "";
            keys.forEach(key => {
                if (state[key] === true) {
                    previousTab = key;
                }
            });
            const previousElement =
                state[previousTab] === true ? document?.querySelector(`[data-test-key="${previousTab}"]`) : undefined;
            const previousContentSelector = previousElement
                ? previousElement?.children[0]?.getAttribute("aria-Controls")
                : undefined;
            const previousContent =
                previousContentSelector && previousContentSelector.length > 0
                    ? document?.getElementById(previousContentSelector)
                    : undefined;

            const expanded = state[tabKey];
            setCurrentTab(tabKey);
            if (expanded !== true) {
                const elem = document?.querySelector(`[data-test-key="${tabKey}"]`);
                if (elem) {
                    const elemBounds = elem.getBoundingClientRect();
                    let offset = 0;
                    const previousBounds = previousElement?.getBoundingClientRect();

                    if (previousElement) {
                        if (previousBounds && previousBounds.top < elemBounds.top) {
                            if (previousContent) {
                                const previousContentBounds = previousContent.getBoundingClientRect();
                                const visibleContentHeight = previousContentBounds.height;
                                offset = visibleContentHeight;
                            }
                        }
                    }
                    const y = elemBounds.top + window.pageYOffset - offset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
            }
        }
    };

    const renderAttribute = (attributeType: AttributeTypeModel) => {
        if (attributeType.name.toLowerCase() === "countryoforigin") {
            if (attributeType?.attributeValues?.some(attributeValue => attributeValue.value.toLowerCase() === "usa")) {
                return (
                    <Typography
                        key={attributeType.id.toString()}
                        data-test-selector="attributes_item"
                        data-attributetypeid={attributeType.id}
                        {...styles.attributeTypeValuesText}
                        as="div"
                    >
                        <LazyImage
                            src="https://pim-prod20190821211516565500000001.s3.amazonaws.com/5dad09d1-91ff-eb11-b563-2818783e793e/1d1af16f21ce01f_sm.jpg"
                            style={{ marginRight: "1rem" }}
                        />
                    </Typography>
                );
            }
            return <></>;
        }
        if (attributeType.name === "CA_Prop_65") {
            if (attributeType?.attributeValues?.some(attributeValue => attributeValue.value.toLowerCase() === "yes")) {
                return (
                    <Link key={attributeType.id} href="http://www.p65warnings.ca.gov" target="_blank">
                        <LazyImage
                            height="40px"
                            src="https://pim-prod20190821211516565500000001.s3.amazonaws.com/5dad09d1-91ff-eb11-b563-2818783e793e/c4ac890e30cee87_md.png"
                            style={{ marginRight: "1rem" }}
                        />
                    </Link>
                );
            }
            return <></>;
        }
    };

    const renderTabs = () => {
        const allAttributes: AttributeTypeModel[] = createAttributes(attributeTypes);

        const content = [];
        const tabs = [];

        if (sortedSpecifications) {
            sortedSpecifications?.forEach(specification => {
                if (specification) {
                    let htmlContent = specification.htmlContent;
                    if (htmlContent.includes("<=")) {
                        htmlContent = htmlContent.replace(/<=/g, "&lt=");
                    }
                    if (htmlContent.includes("=<")) {
                        htmlContent = htmlContent.replace(/=</g, "=&lt");
                    }
                    if (htmlContent.includes(">=")) {
                        htmlContent = htmlContent.replace(/>=/g, "&gt=");
                    }
                    if (htmlContent.includes("=>")) {
                        htmlContent = htmlContent.replace(/=>/g, "=&gt");
                    }
                    tabs.push(
                        <Tab
                            style={specification === undefined ? { display: "none" } : {}}
                            headline={
                                <Typography {...styles.tabContent}>
                                    <Icon
                                        width={15}
                                        height={15}
                                        src={state[specification.name] ? MinusCircle : PlusCircle}
                                    />
                                    <Typography {...styles.tabContent}>{translate(specification.name)}</Typography>
                                </Typography>
                            }
                            key={specification.name}
                            tabKey={specification.name}
                            hidden={false}
                        >
                            {state[specification.name] ? (
                                <Typography {...styles.tabContent}>{parse(htmlContent, parserOptions)}</Typography>
                            ) : (
                                <></>
                            )}
                        </Tab>,
                    );
                }
            });
        }
        if (documents) {
            tabs.push(
                <Tab
                    {...styles.tabDocumentContainer}
                    style={documents === undefined || documents?.length === 0 ? { display: "none" } : {}}
                    headline={
                        <Typography {...styles.tabContent}>
                            <Icon width={15} height={15} src={state["Documents"] ? MinusCircle : PlusCircle} />
                            <Typography {...styles.tabContent}>{translate("Documents")}</Typography>
                        </Typography>
                    }
                    key="Documents"
                    tabKey="Documents"
                >
                    {state["Documents"] ? (
                        <StyledWrapper {...styles.wrapper}>
                            <GridContainer {...styles.docContainer}>
                                {documents?.map(document => (
                                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                        <Link
                                            href={document.filePath}
                                            target="_new"
                                            {...styles.documentLink}
                                            data-test-selector={`productDetails_${document.id}`}
                                        >
                                            <IconMemo {...styles.documentIcon} />
                                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                                        </Link>
                                    </GridItem>
                                ))}
                            </GridContainer>
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                </Tab>,
            );
        }
        tabs.push(
            <Tab
                headline={
                    <Typography {...styles.tabContent}>
                        <Icon width={15} height={15} src={state["Options"] ? MinusCircle : PlusCircle} />
                        <Typography {...styles.tabContent}>{translate("Options")}</Typography>
                    </Typography>
                }
                key="Options"
                tabKey="Options"
            >
                {state["Options"] ? (
                    <StyledWrapper {...styles.wrapper}>
                        <GridContainer {...styles.docContainer}>
                            {canAddToWishlist ? (
                                <GridItem {...styles.optionsGridItem}>
                                    <ProductAddToListLink></ProductAddToListLink>
                                    <Icon {...styles.optionsIcon} width={15} height={15} src={List} />
                                </GridItem>
                            ) : (
                                <></>
                            )}
                            <GridItem {...styles.optionsGridItem}>
                                <ProductShareLink></ProductShareLink>
                                <Icon {...styles.optionsIcon} width={15} height={15} src={Share} />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                ) : (
                    <></>
                )}
            </Tab>,
        );
        tabs.push(
            <Tab {...styles.hiddenTabContainer} headline={translate("None")} key="None" tabKey="None" hidden={true}>
                <></>
            </Tab>,
        );

        return (
            <TabGroup current={currentTab} onTabChange={onTabChange}>
                {tabs}
            </TabGroup>
        );
    };

    const renderAccordion = () => {
        const allAttributes: AttributeTypeModel[] = createAttributes(attributeTypes);

        const content = [];
        const tabs = [];

        if (sortedSpecifications) {
            sortedSpecifications?.forEach(specification => {
                if (specification) {
                    let htmlContent = specification.htmlContent;
                    if (htmlContent.includes("<=")) {
                        htmlContent = htmlContent.replace(/<=/g, "&lt=");
                    }
                    if (htmlContent.includes("=<")) {
                        htmlContent = htmlContent.replace(/=</g, "=&lt");
                    }
                    if (htmlContent.includes(">=")) {
                        htmlContent = htmlContent.replace(/>=/g, "&gt=");
                    }
                    if (htmlContent.includes("=>")) {
                        htmlContent = htmlContent.replace(/=>/g, "=&gt");
                    }
                    tabs.push(
                        <AccordionSection
                            key={specification.name}
                            // {...styles.brandAccordionSection}
                            title={specification.name}
                            expanded={state[specification.name]}
                            onTogglePanel={onClickAccordionSection(specification.name)}
                        >
                            {state[specification.name] ? (
                                <Typography {...styles.tabContent}>{parse(htmlContent, parserOptions)}</Typography>
                            ) : (
                                <></>
                            )}
                        </AccordionSection>,
                    );
                }
            });
        }
        if (documents && documents.length && documents.length > 0) {
            tabs.push(
                <AccordionSection
                    key="Documents"
                    // {...styles.brandAccordionSection}
                    title="Documents"
                    expanded={state["Documents"]}
                    onTogglePanel={onClickAccordionSection("Documents")}
                >
                    {state["Documents"] ? (
                        <StyledWrapper {...styles.wrapper}>
                            <GridContainer {...styles.docContainer}>
                                {documents?.map(document => (
                                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                        <Link
                                            href={document.filePath}
                                            target="_new"
                                            {...styles.documentLink}
                                            data-test-selector={`productDetails_${document.id}`}
                                        >
                                            <IconMemo {...styles.documentIcon} />
                                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                                        </Link>
                                    </GridItem>
                                ))}
                            </GridContainer>
                        </StyledWrapper>
                    ) : (
                        <></>
                    )}
                </AccordionSection>,
            );
        }

        tabs.push(
            <AccordionSection
                key="Options"
                // {...styles.brandAccordionSection}
                title="Options"
                expanded={state["Options"]}
                onTogglePanel={onClickAccordionSection("Options")}
            >
                {state["Options"] ? (
                    <StyledWrapper {...styles.wrapper}>
                        <GridContainer {...styles.docContainer}>
                            {canAddToWishlist ? (
                                <GridItem {...styles.optionsGridItem}>
                                    <ProductAddToListLink></ProductAddToListLink>
                                    <Icon {...styles.optionsIcon} width={15} height={15} src={List} />
                                </GridItem>
                            ) : (
                                <></>
                            )}
                            <GridItem {...styles.optionsGridItem}>
                                <ProductShareLink></ProductShareLink>
                                <Icon {...styles.optionsIcon} width={15} height={15} src={Share} />
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                ) : (
                    <></>
                )}
            </AccordionSection>,
        );

        return <Accordion headingLevel={2}>{tabs}</Accordion>;
    };

    const createAttributes = (attributeTypes: AttributeTypeModel[] | undefined): AttributeTypeModel[] => {
        if (!attributeTypes) {
            return [] as AttributeTypeModel[];
        }
        const attributes: AttributeTypeModel[] = [];

        attributeTypes.forEach(attributeType => {
            if (
                attributeType.name.toLowerCase() !== "ca_prop_65" &&
                attributeType.name.toLowerCase() !== "countryoforigin"
            ) {
                attributes.push(attributeType);
            }
        });
        const imageAttributes: AttributeTypeModel[] = [];
        attributeTypes.forEach(attributeType => {
            if (
                (attributeType.name.toLowerCase() === "ca_prop_65" &&
                    attributeType?.attributeValues?.some(
                        attributeValue => attributeValue.value.toLowerCase() === "yes",
                    )) ||
                (attributeType.name.toLowerCase() === "countryoforigin" &&
                    attributeType?.attributeValues?.some(
                        attributeValue => attributeValue.value.toLowerCase() === "usa",
                    ))
            ) {
                attributes.push(attributeType);
            }
        });
        return attributes;
    };

    return (
        <>
            <Hidden below="md">{renderTabs()}</Hidden>
            <Hidden above="sm">{renderAccordion()}</Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsCollapsibleTabs),
    definition: {
        displayName: "Collapsible Tabs",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
